import { useState, useEffect, useCallback } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('useAdminTableState')

/**
 * Reusable hook for admin table state management
 * Consolidates search, filter, and pagination logic
 * 
 * @param {Function} loadDataFn - Async function to load data (receives params object)
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.pageSize - Items per page (default: 20)
 * @param {number} options.debounceMs - Search debounce delay (default: 500)
 * @returns {Object} Table state and handlers
 */
export function useAdminTableState(loadDataFn, options = {}) {
  const {
    initialPage = 1,
    pageSize = 20,
    debounceMs = 500,
    initialFilter = 'all'
  } = options

  // Search state
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  
  // Filter state
  const [filter, setFilter] = useState(initialFilter)
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: initialPage,
    total: 0,
    totalPages: 0
  })
  
  // Data state
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPagination(prev => ({ ...prev, page: 1 }))
    }, debounceMs)
    
    return () => clearTimeout(timer)
  }, [searchInput, debounceMs])

  // Load data when dependencies change
  const loadData = useCallback(async (page = pagination.page) => {
    setLoading(true)
    setError(null)
    
    try {
      const params = {
        page,
        limit: pageSize
      }
      
      if (search.trim()) {
        params.search = search.trim()
      }
      
      if (filter !== 'all') {
        params.filter = filter
      }
      
      const result = await loadDataFn(params)
      
      setData(result.data || [])
      setPagination({
        page: result.pagination?.page || page,
        total: result.pagination?.total || 0,
        totalPages: result.pagination?.totalPages || Math.ceil((result.pagination?.total || 0) / pageSize)
      })
    } catch (err) {
      logger.error('Failed to load data', err)
      setError(err.message || 'Failed to load data')
      setData([])
    } finally {
      setLoading(false)
    }
  }, [loadDataFn, pagination.page, pageSize, search, filter])

  // Reload on search/filter change
  useEffect(() => {
    loadData(1)
  }, [search, filter])

  // Reload on page change
  useEffect(() => {
    loadData(pagination.page)
  }, [pagination.page])

  // Handlers
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value)
  }, [])

  const handleFilterChange = useCallback((value) => {
    setFilter(value)
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  const handlePageChange = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }))
  }, [])

  const refetch = useCallback(() => {
    loadData(pagination.page)
  }, [loadData, pagination.page])

  return {
    // State
    data,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    
    // Handlers
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  }
}

export default useAdminTableState
